import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Lock, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/** Strength score 0..4 from a password */
function pwScore(pw) {
  if (!pw) return 0;
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

const STRENGTH_META = [
  { label: 'Very weak', color: 'bg-rose-500',   text: 'text-rose-500' },
  { label: 'Weak',      color: 'bg-orange-500', text: 'text-orange-500' },
  { label: 'Fair',      color: 'bg-amber-500',  text: 'text-amber-500' },
  { label: 'Strong',    color: 'bg-emerald-500',text: 'text-emerald-500' },
  { label: 'Excellent', color: 'bg-emerald-600',text: 'text-emerald-600' },
];

/**
 * Set Password screen — used after invite acceptance and after any login
 * where `must_change_password` is true.
 */
export default function SetPasswordPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isWelcome = params.get('welcome') === '1';

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const score = useMemo(() => pwScore(pw), [pw]);
  const meta = STRENGTH_META[score];
  const matches = pw && pw === confirm;
  const canSubmit = pw.length >= 8 && matches && score >= 2 && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { new_password: pw });
      await refreshUser();
      toast.success('Password set successfully');
      // For invited team members, route to orientation; otherwise dashboard
      const role = user?.role;
      if (isWelcome || role === 'team_member') {
        navigate('/welcome', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not set password';
      toast.error(typeof detail === 'string' ? detail : 'Could not set password');
    } finally {
      setSubmitting(false);
    }
  };

  const firstName = (user?.full_name || '').split(' ')[0] || '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full" data-testid="set-password-card">
        <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-7">
          <div className="mb-5 text-center">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-3 shadow-lg shadow-emerald-500/20">
              <Lock className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {isWelcome ? `Welcome${firstName ? `, ${firstName}` : ''}!` : 'Set a new password'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isWelcome
                ? 'Your account is ready. Set a password you\'ll use from now on.'
                : 'For your security, please set a new password.'}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-pw" className="text-xs font-medium text-slate-700">New password</Label>
              <div className="relative">
                <Input
                  id="new-pw"
                  type={showPw ? 'text' : 'password'}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="At least 8 characters"
                  autoFocus
                  autoComplete="new-password"
                  data-testid="new-password-input"
                  required
                  minLength={8}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                  data-testid="toggle-pw-visibility"
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                >
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              {/* Strength meter */}
              {pw && (
                <div className="mt-2" data-testid="pw-strength">
                  <div className="flex gap-1 h-1.5">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className={`flex-1 rounded-full transition-colors ${i < score ? meta.color : 'bg-slate-200'}`} />
                    ))}
                  </div>
                  <p className={`mt-1 text-[11px] font-medium ${meta.text}`}>{meta.label}</p>
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-pw" className="text-xs font-medium text-slate-700">Confirm password</Label>
              <Input
                id="confirm-pw"
                type={showPw ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Type it again"
                autoComplete="new-password"
                data-testid="confirm-password-input"
                required
                minLength={8}
                aria-invalid={confirm && !matches ? 'true' : 'false'}
              />
              {confirm && !matches && (
                <p className="text-[11px] text-rose-500">Passwords do not match.</p>
              )}
              {confirm && matches && (
                <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Match
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 mt-2"
              data-testid="set-password-submit"
            >
              {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : <><Sparkles className="h-4 w-4 mr-2" /> Set password & continue</>}
            </Button>

            <ul className="text-[11px] text-slate-500 space-y-0.5 mt-3 pl-1">
              <li>• Minimum 8 characters</li>
              <li>• Mix of upper and lower case improves strength</li>
              <li>• Add a number and a symbol for an excellent score</li>
            </ul>
          </form>
        </div>
      </div>
    </div>
  );
}
